/**
 * P1 · Tab Fatture Passive Fornitori — Dettaglio Subappalto.
 *
 * Scope MVP:
 *  - Lista fatture del subappalto con totali derivati (assigned/invoiced/paid/remaining).
 *  - Creazione nuova fattura via SupplierInvoiceCreateModal.
 *  - Cambio stato amministrativo (ricevuta → registrata → approvata / annullata / contestata).
 *  - Sezione pagamenti per singola fattura.
 *  - Nessun campo derivato è salvato: `invoicing_status`, `payment_status`, `remaining_*` sono
 *    calcolati dal backend e mostrati read-only.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { RefreshCw, Plus, FileText } from 'lucide-react';
import { fmtEUR } from '../../lib/contractModificationApi';
import SupplierInvoiceCreateModal from './SupplierInvoiceCreateModal';
import SupplierInvoiceRow from './SupplierInvoiceRow';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => {
  const t = localStorage.getItem('token');
  return { headers: t ? { Authorization: `Bearer ${t}` } : {} };
};

const STATUS_COLORS = {
  non_fatturato: 'bg-slate-100 text-slate-700',
  parzialmente_fatturato: 'bg-amber-100 text-amber-800',
  completamente_fatturato: 'bg-emerald-100 text-emerald-800',
  eccedenza_fatturata: 'bg-red-100 text-red-800',
  non_pagato: 'bg-slate-100 text-slate-700',
  parzialmente_pagato: 'bg-amber-100 text-amber-800',
  completamente_pagato: 'bg-emerald-100 text-emerald-800',
};

const STATUS_LABELS = {
  non_fatturato: 'Non fatturato',
  parzialmente_fatturato: 'Parzialmente fatturato',
  completamente_fatturato: 'Completamente fatturato',
  eccedenza_fatturata: 'Eccedenza fatturata ⚠',
  non_pagato: 'Non pagato',
  parzialmente_pagato: 'Parzialmente pagato',
  completamente_pagato: 'Completamente pagato',
};

const SupplierInvoicesTab = ({ subcontract, onSubcontractChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    if (!subcontract?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/subcontracts/${subcontract.id}/invoices`,
        auth()
      );
      setData(r.data);
    } catch (e) {
      toast.error('Errore caricamento fatture');
    } finally {
      setLoading(false);
    }
  }, [subcontract?.id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="text-sm text-slate-500 p-4">Caricamento fatture…</div>;
  }
  if (!data) return null;

  const t = data.totals || {};
  const canCreate = subcontract?.status === 'firmato';

  return (
    <div className="space-y-4" data-testid="supplier-invoices-tab">
      {/* Header + Totali */}
      <div className="rounded-md border bg-white p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <h3 className="text-base font-semibold flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-500" />
            Fatture Fornitore
          </h3>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={load} data-testid="si-reload">
              <RefreshCw className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              onClick={() => setShowCreate(true)}
              disabled={!canCreate}
              title={!canCreate ? 'Il subappalto deve essere firmato' : ''}
              data-testid="si-new-invoice"
            >
              <Plus className="w-3 h-3 mr-1" /> Nuova fattura
            </Button>
          </div>
        </div>

        {/* Riepilogo economico derivato */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
          <MetricCard label="Affidato" value={fmtEUR(t.assigned_cost)} testId="si-total-assigned" />
          <MetricCard label="Fatturato" value={fmtEUR(t.invoiced_cost)} testId="si-total-invoiced" />
          <MetricCard label="Pagato" value={fmtEUR(t.paid_cost)} testId="si-total-paid" />
          <MetricCard
            label="Residuo da fatturare"
            value={fmtEUR(t.remaining_to_invoice)}
            testId="si-remaining-invoice"
            danger={t.remaining_to_invoice < 0}
          />
          <MetricCard
            label="Residuo da pagare"
            value={fmtEUR(t.remaining_to_pay)}
            testId="si-remaining-pay"
          />
        </div>

        <div className="mt-3 flex gap-2 flex-wrap">
          <Badge className={STATUS_COLORS[t.invoicing_status] || 'bg-slate-100'} data-testid="si-invoicing-status">
            {STATUS_LABELS[t.invoicing_status] || t.invoicing_status}
          </Badge>
          <Badge className={STATUS_COLORS[t.payment_status] || 'bg-slate-100'} data-testid="si-payment-status">
            {STATUS_LABELS[t.payment_status] || t.payment_status}
          </Badge>
        </div>
      </div>

      {/* Lista fatture */}
      {(data.invoices || []).length === 0 ? (
        <div className="rounded-md border bg-slate-50 p-6 text-center text-sm text-slate-500">
          Nessuna fattura registrata per questo subappalto.
        </div>
      ) : (
        <div className="rounded-md border bg-white overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs font-medium text-slate-500 border-b bg-slate-50">
            <div className="col-span-2">Numero</div>
            <div className="col-span-2">Data / Scadenza</div>
            <div className="col-span-2">Fornitore</div>
            <div className="col-span-2 text-right">Imponibile</div>
            <div className="col-span-2 text-right">Pagato</div>
            <div className="col-span-2">Stato</div>
          </div>
          {data.invoices.map((inv) => (
            <SupplierInvoiceRow key={inv.id} invoice={inv} onChanged={load} />
          ))}
        </div>
      )}

      {showCreate && (
        <SupplierInvoiceCreateModal
          subcontract={subcontract}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
};

const MetricCard = ({ label, value, testId, danger }) => (
  <div className="rounded-md border bg-slate-50 px-3 py-2" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`text-sm font-semibold ${danger ? 'text-red-700' : 'text-slate-800'}`}>
      {value}
    </div>
  </div>
);

export default SupplierInvoicesTab;
